import React, { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import { tokens } from "../../../Shared/tokens";
import { useLazyGetEmployeeDailyReportQuery } from "../../../features/raceReportSlice";

/**
 * Per-employee detail for the race board.
 *
 * The hourly breakdown is the part that makes the board an observation tool
 * rather than a ranking: it shows *when* the day went quiet, which a single
 * daily score cannot.
 */
export const EmployeeRaceDrawer = ({ employee, date, onClose }) => {
  const [fetchReport, { data, isFetching, isError }] =
    useLazyGetEmployeeDailyReportQuery();

  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);

  const open = Boolean(employee);

  useEffect(() => {
    if (!employee) return;
    fetchReport({ employeeId: employee.employeeId, date });
  }, [employee, date, fetchReport]);

  useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement;
    const timer = setTimeout(() => panelRef.current?.focus(), 50);
    return () => {
      clearTimeout(timer);
      returnFocusRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [open]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  const hours = data?.hourlyActivity ?? [];
  const peak = hours.reduce((max, h) => Math.max(max, h.leadsWorked), 0);
  // Only meaningful once the score is loaded; fall back to the row's own
  // numbers so the header never sits empty while fetching.
  const calls = data?.connectedCalls ?? employee.connectedCalls;
  const meetings = data?.meetingsCompleted ?? employee.meetingsCompleted;
  const score = data?.score ?? employee.score;

  // Portalled to body: the dashboard sits inside a stacking context created
  // higher up, which traps a fixed overlay underneath the app header no matter
  // how large its z-index is.
  return createPortal(
    <Overlay
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <Panel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${employee.employeeName} daily activity`}
        tabIndex={-1}
      >
        <Header>
          <div>
            <h3>{employee.employeeName}</h3>
            <p>{moment(date).format("dddd, D MMM YYYY")}</p>
          </div>
          <CloseButton type="button" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </CloseButton>
        </Header>

        <Summary>
          <SummaryItem>
            <dt>Score</dt>
            <dd>
              {score}
              <small> / 100</small>
            </dd>
          </SummaryItem>
          <SummaryItem>
            <dt>
              <CallOutlinedIcon fontSize="inherit" aria-hidden="true" /> Calls
            </dt>
            <dd>
              {calls}
              <small> / 30</small>
            </dd>
          </SummaryItem>
          <SummaryItem>
            <dt>
              <HandshakeOutlinedIcon fontSize="inherit" aria-hidden="true" />{" "}
              Meetings
            </dt>
            <dd>{meetings}</dd>
          </SummaryItem>
          <SummaryItem>
            <dt>Rank</dt>
            {/* A zero score means everyone is still tied at position 1, so the
                number would claim a lead nobody has. */}
            <dd>{score > 0 ? `#${employee.position}` : "–"}</dd>
          </SummaryItem>
        </Summary>

        <Section>
          <h4>Leads worked by hour</h4>

          {isFetching && <Note>Loading hourly activity…</Note>}
          {isError && !isFetching && (
            <Note $tone="danger">Could not load hourly activity.</Note>
          )}
          {!isFetching && !isError && !hours.length && (
            <Note>No hourly activity recorded for this day.</Note>
          )}

          {!isFetching && !isError && hours.length > 0 && (
            <Hours>
              {hours.map((slot) => (
                <HourRow key={slot.hour}>
                  <HourLabel>{slot.hour}</HourLabel>
                  <HourTrack>
                    <HourFill
                      $empty={slot.leadsWorked === 0}
                      style={{
                        width: peak
                          ? `${Math.max((slot.leadsWorked / peak) * 100, slot.leadsWorked ? 6 : 0)}%`
                          : "0%",
                      }}
                    />
                  </HourTrack>
                  <HourValue $empty={slot.leadsWorked === 0}>
                    {slot.leadsWorked}
                  </HourValue>
                </HourRow>
              ))}
            </Hours>
          )}

          <Footnote>
            Counts unique leads worked per slot. The 13:30–14:30 break is not
            tracked.
          </Footnote>
        </Section>
      </Panel>
    </Overlay>,
    document.body
  );
};

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideIn = keyframes`
  from { opacity: 0; transform: translateX(16px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background-color: rgba(17, 24, 39, 0.45);
  display: flex;
  justify-content: flex-end;
  animation: ${fadeIn} ${tokens.motion.base} ${tokens.motion.ease};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Panel = styled.div`
  width: min(420px, 100%);
  height: 100%;
  overflow-y: auto;
  background-color: ${tokens.color.surface};
  box-shadow: ${tokens.shadow.overlay};
  padding: ${tokens.space.xl};
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.xl};
  outline: none;
  animation: ${slideIn} ${tokens.motion.slow} ${tokens.motion.ease};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${tokens.space.md};

  h3 {
    margin: 0;
    font-size: ${tokens.font.xl};
    color: ${tokens.color.textPrimary};
  }

  p {
    margin: ${tokens.space.xs} 0 0;
    font-size: ${tokens.font.md};
    color: ${tokens.color.textMuted};
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${tokens.touchTarget};
  height: ${tokens.touchTarget};
  flex-shrink: 0;
  color: ${tokens.color.textMuted};
  background: none;
  border: none;
  border-radius: ${tokens.radius.md};
  cursor: pointer;
  outline: none;

  :hover {
    background-color: ${tokens.color.surfaceHover};
    color: ${tokens.color.textPrimary};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;

const Summary = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${tokens.space.md};
  margin: 0;
`;

const SummaryItem = styled.div`
  padding: ${tokens.space.md};
  background-color: ${tokens.color.surfaceSubtle};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.md};

  dt {
    display: flex;
    align-items: center;
    gap: ${tokens.space.xs};
    font-size: ${tokens.font.sm};
    color: ${tokens.color.textMuted};
  }

  dd {
    margin: ${tokens.space.xs} 0 0;
    font-size: 20px;
    font-weight: 600;
    color: ${tokens.color.textPrimary};

    small {
      font-size: ${tokens.font.sm};
      font-weight: 400;
      color: ${tokens.color.textMuted};
    }
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.md};

  h4 {
    margin: 0;
    font-size: ${tokens.font.md};
    color: ${tokens.color.textPrimary};
  }
`;

const Note = styled.p`
  margin: 0;
  font-size: ${tokens.font.md};
  color: ${(p) =>
    p.$tone === "danger" ? tokens.color.danger : tokens.color.textMuted};
`;

const Hours = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.sm};
`;

const HourRow = styled.div`
  display: grid;
  grid-template-columns: 96px 1fr 24px;
  align-items: center;
  gap: ${tokens.space.sm};
`;

const HourLabel = styled.span`
  font-size: ${tokens.font.xs};
  color: ${tokens.color.textMuted};
  font-variant-numeric: tabular-nums;
`;

const HourTrack = styled.span`
  height: 8px;
  background-color: ${tokens.color.surfaceHover};
  border-radius: ${tokens.radius.pill};
  overflow: hidden;
`;

const HourFill = styled.span`
  display: block;
  height: 100%;
  border-radius: inherit;
  background-color: ${(p) =>
    p.$empty ? tokens.color.border : tokens.color.primary};
  transition: width 300ms ${tokens.motion.ease};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HourValue = styled.span`
  font-size: ${tokens.font.sm};
  font-weight: 600;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$empty ? tokens.color.textMuted : tokens.color.textPrimary)};
`;

const Footnote = styled.p`
  margin: 0;
  font-size: ${tokens.font.xs};
  line-height: 1.5;
  color: ${tokens.color.textMuted};
`;
